import React, { useState, useEffect } from "react";
import { Upload, Button, message, Table, Card, Typography, Empty, Spin } from "antd";
import { UploadOutlined, CheckCircleOutlined } from "@ant-design/icons";
import Papa from "papaparse";
import { RcFile } from "antd/es/upload";
import type { ColumnsType } from "antd/es/table";
import CustomExcelIcon from "../../../assets/icons/CustomExcelIcon";

const { Text } = Typography;

interface StepUploadProps {
  onNext: (data: StepUploadData) => void;
  existingData?: StepUploadData | null;
  // True when opened via "View" on an already-created job: there's no file in
  // this browser session to re-upload or re-parse, so this renders a read-only
  // summary of the columns that were mapped for this study instead of the
  // upload dropzone. viewColumns is a list of "Label: column name" pairs the
  // parent derives from the job's saved config; onViewNext just advances to
  // Configuration (no StepUploadData to produce, unlike the creation flow's
  // onNext).
  viewMode?: boolean;
  viewColumns?: { label: string; value: string }[];
  onViewNext?: () => void;
  onPrevious?: () => void;
}

export interface StepUploadData {
  dataFile: RcFile;
  headers: string[];
  previewData: any[];
}

// Mirrors Bid Optimizer's StepOne.tsx - Causal Loop uploads a single CSV that
// powers both engines (see causal-loop-feature/plan.md), so the same parse/preview
// logic applies; the file is copied to both engines' expected S3 input paths in
// StepConfiguration's submit step.
const StepUpload: React.FC<StepUploadProps> = ({
  onNext,
  existingData,
  viewMode,
  viewColumns,
  onViewNext,
  onPrevious,
}) => {
  const [fileList, setFileList] = useState<RcFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [isParsingFile, setIsParsingFile] = useState(false);

  useEffect(() => {
    if (existingData) {
      setFileList([existingData.dataFile]);
      setHeaders(existingData.headers);
      setPreviewData(existingData.previewData);
    }
  }, [existingData]);

  const parseFile = (file: RcFile) => {
    setIsParsingFile(true);

    Papa.parse(file, {
      header: true,
      preview: 10,
      complete: (results) => {
        if (results.data && results.data.length > 0 && results.meta.fields) {
          setHeaders(results.meta.fields);
          setPreviewData(results.data);
        } else {
          message.error("Failed to parse CSV file. Please check the file format.");
        }
        setIsParsingFile(false);
      },
      error: (error) => {
        console.error("Error parsing CSV:", error);
        message.error("Error parsing CSV file. Please check the file format.");
        setIsParsingFile(false);
      },
    });
  };

  const handleUpload = () => {
    if (fileList.length === 0) {
      message.error("Please select a CSV file first");
      return;
    }

    setUploading(true);

    Papa.parse(fileList[0], {
      header: true,
      complete: (results) => {
        if (results.data && results.data.length > 0 && results.meta.fields) {
          const parsedHeaders = results.meta.fields;
          onNext({
            dataFile: fileList[0],
            headers: parsedHeaders,
            previewData: previewData.length > 0 ? previewData : results.data.slice(0, 10),
          });
        } else {
          message.error("Failed to parse CSV file. Please check the file format.");
        }
        setUploading(false);
      },
      error: (error) => {
        console.error("Error parsing CSV:", error);
        message.error("Error parsing CSV file. Please check the file format.");
        setUploading(false);
      },
    });
  };

  const props = {
    onRemove: () => {
      setFileList([]);
      setHeaders([]);
      setPreviewData([]);
    },
    beforeUpload: (file: RcFile) => {
      const isCsv = file.type === "text/csv" || file.name.endsWith(".csv");
      if (!isCsv) {
        message.error("You can only upload CSV files!");
        return false;
      }
      setFileList([file]);
      parseFile(file);
      return false;
    },
    fileList,
  };

  const columns: ColumnsType<any> = headers.map((header) => ({
    title: header,
    dataIndex: header,
    key: header,
    ellipsis: true,
  }));

  if (viewMode) {
    return (
      <div className="flex flex-col space-y-6">
        <Card className="shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Data</h3>
          <Text className="text-sm text-gray-600 block mb-6">
            This study's data was already uploaded and processed - the file itself isn't kept in
            the browser to re-preview, but here's how its columns were mapped.
          </Text>

          {viewColumns && viewColumns.length > 0 ? (
            <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200">
              {viewColumns.map((c) => (
                <div key={c.label} className="flex justify-between px-4 py-3">
                  <Text className="text-sm text-gray-500">{c.label}</Text>
                  <Text className="text-sm font-medium text-gray-900">{c.value}</Text>
                </div>
              ))}
            </div>
          ) : (
            <Empty description="No column mapping found" image={Empty.PRESENTED_IMAGE_SIMPLE} className="my-8" />
          )}
        </Card>

        <div className="flex justify-between">
          <Button size="large" onClick={onPrevious}>
            Previous
          </Button>
          <Button type="primary" size="large" onClick={onViewNext}>
            Next
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6">
      <Card className="shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Data</h3>
        <Text className="text-sm text-gray-600 block mb-6">
          Upload one CSV file - it powers both the Bid Optimizer and Geo Lift engines. You'll
          map which columns feed each engine in the next step.
        </Text>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-6">
          <div className="flex items-start">
            <CustomExcelIcon className="text-blue-500 text-xl mt-1 mr-3" />
            <div>
              <Text strong className="text-blue-700 block mb-1">File Requirements</Text>
              <Text className="text-blue-600 block text-sm">
                • File must be in CSV format<br />
                • First row should contain column headers<br />
                • Include a date column, a location/DMA column, and a target value column<br />
                • Optional: demographic, geographic, and device data columns
              </Text>
            </div>
          </div>
        </div>

        <Upload {...props} maxCount={1} accept=".csv" className="mb-6" listType="picture">
          <Button icon={<UploadOutlined />} size="large" type="primary" ghost>
            Select CSV File
          </Button>
        </Upload>

        {isParsingFile && (
          <div className="text-center py-8">
            <Spin tip="Parsing file..." />
          </div>
        )}

        {!isParsingFile && headers.length > 0 && previewData.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center mb-3">
              <CheckCircleOutlined className="text-green-500 mr-2" />
              <Text strong className="text-green-700">File successfully parsed</Text>
            </div>

            <Card
              title="Data Preview"
              size="small"
              className="mb-4"
              extra={<Text type="secondary">Showing first {previewData.length} rows</Text>}
            >
              <div className="max-h-[300px] overflow-auto">
                <Table
                  dataSource={previewData}
                  columns={columns}
                  size="small"
                  pagination={false}
                  bordered
                  scroll={{ x: "max-content" }}
                  rowKey={(record, index) => index?.toString() || "0"}
                />
              </div>
            </Card>
          </div>
        )}

        {!isParsingFile && headers.length === 0 && !fileList.length && (
          <Empty
            description="No file uploaded yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            className="my-8"
          />
        )}

        <div className="mt-6 flex justify-end">
          <Button
            type="primary"
            onClick={handleUpload}
            loading={uploading}
            disabled={fileList.length === 0 || headers.length === 0}
            size="large"
          >
            Continue to Configuration
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default StepUpload;
